import { useState, useRef, useEffect } from 'react'
import { Spin, Typography, theme, Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import { App } from 'antd'
import { SurveyStepRole } from '../components/Auth/SurveyStepRole'
import { SurveyStepUseCases } from '../components/Auth/SurveyStepUseCases'
import { SurveyStepFrequency } from '../components/Auth/SurveyStepFrequency'
import { useProfileContext } from '../context/profile'
import {
  FlowContainer,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  StepIndicator,
  StepContent,
  NavigationButtons,
  BackButton,
  ContinueButton,
} from '../components/Auth/styles/SignUpFlowStyles'
import { PageContainer, CardContainer, CenteredTitle, LogoLink } from './styles/LoginPageStyles'

const { useToken } = theme

const TOTAL_STEPS = 3
const AUTO_ADVANCE_DELAY_MS = 280

export function SurveyPage() {
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const { token } = useToken()
  const { updateProfile } = useProfileContext()

  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // Step 1: Who is this workspace for? → survey_user_role
  const [surveyUserRole, setSurveyUserRole] = useState<string | null>(null)
  // Step 2: Where is your project at? → survey_ideation_frequency
  const [surveyProjectStage, setSurveyProjectStage] = useState<string | null>(null)
  // Step 3: What would make today a win? → survey_use_cases (max 2)
  const [surveySessionGoals, setSurveySessionGoals] = useState<string[]>([])

  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
    }
  }, [])

  const scheduleAdvance = (toStep: number) => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
    autoAdvanceTimer.current = setTimeout(() => {
      setCurrentStep(toStep)
      autoAdvanceTimer.current = null
    }, AUTO_ADVANCE_DELAY_MS)
  }

  const handleRoleSelect = (value: string) => {
    setSurveyUserRole(value)
    scheduleAdvance(2)
  }

  const handleProjectStageSelect = (value: string) => {
    setSurveyProjectStage(value)
    scheduleAdvance(3)
  }

  const canFinish = surveySessionGoals.length > 0

  const handleSkip = async () => {
    setLoading(true)
    const { error } = await updateProfile({ survey_user_role: 'skipped' })
    setLoading(false)
    if (error) {
      notification.error({
        title: 'Something went wrong',
        description: 'Could not skip the survey. Please try again.',
      })
      return
    }
    void navigate('/')
  }

  const handleFinish = async () => {
    setLoading(true)
    const { error } = await updateProfile({
      survey_user_role: surveyUserRole ?? '',
      survey_ideation_frequency: surveyProjectStage ?? '',
      survey_use_cases: surveySessionGoals,
    })
    setLoading(false)

    if (error) {
      notification.error({
        title: 'Something went wrong',
        description: 'Could not save your answers. Please try again.',
      })
      return
    }

    void navigate('/')
  }

  const progress = (currentStep / TOTAL_STEPS) * 100

  return (
    <PageContainer
      align="center"
      justify="center"
      $colorPrimary={token.colorPrimary}
      $colorBgContainer={token.colorBgContainer}
    >
      <LogoLink href="https://bandos.ai" $color={token.colorText}>
        Bandos
      </LogoLink>
      <CardContainer
        $colorBgContainer={token.colorBgContainer}
        $borderRadius={token.borderRadius}
        $boxShadowTertiary={token.boxShadowTertiary}
      >
        <CenteredTitle level={2}>Let's tailor your workspace</CenteredTitle>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}
        >
          Calibrating your AI research assistant
        </Typography.Text>

        <FlowContainer>
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill $progress={progress} />
            </ProgressBar>
            <StepIndicator>
              Step {currentStep} of {TOTAL_STEPS}
            </StepIndicator>
          </ProgressContainer>

          <StepContent key={currentStep}>
            {currentStep === 1 && (
              <SurveyStepRole value={surveyUserRole} onChange={handleRoleSelect} />
            )}
            {currentStep === 2 && (
              <SurveyStepFrequency value={surveyProjectStage} onChange={handleProjectStageSelect} />
            )}
            {currentStep === 3 && (
              <SurveyStepUseCases values={surveySessionGoals} onChange={setSurveySessionGoals} />
            )}
          </StepContent>

          {/* Step 2: just a back link — card click is the primary advance action */}
          {currentStep === 2 && (
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <BackButton onClick={() => setCurrentStep(1)}>← Back</BackButton>
            </div>
          )}

          {/* Step 3: full row with Back + Get Started */}
          {currentStep === 3 && (
            <NavigationButtons>
              <BackButton onClick={() => setCurrentStep(2)}>Back</BackButton>
              <ContinueButton
                onClick={handleFinish}
                disabled={!canFinish || loading}
                $disabled={!canFinish || loading}
              >
                {loading ? <Spin size="small" /> : 'Get Started'}
              </ContinueButton>
            </NavigationButtons>
          )}

          <Button
            type="link"
            size="small"
            onClick={handleSkip}
            disabled={loading}
            style={{
              display: 'block',
              margin: '32px auto 0',
              color: 'var(--color-text-secondary)',
            }}
          >
            Skip survey
          </Button>
        </FlowContainer>
      </CardContainer>
    </PageContainer>
  )
}
